// src/components/auth/ProtectedRoute.tsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, CircularProgress } from '@mui/material';
import { UserRole } from '../../types/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowRoles?: UserRole[];
}

const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowRoles,
}) => {
  const { isAuthenticated, isLoading, user, token, logout } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (token && isTokenExpired(token)) {
    logout();
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (
    allowRoles &&
    (!user?.role || !allowRoles.includes(user.role as UserRole))
  ) {
    // Deliberately does NOT carry state.from — this is a "you're logged in
    // but not allowed here" redirect, not a "please log in first" redirect,
    // and there's no login step downstream to consume it.
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
